/**
 * T155P1 — SuggestionChip
 *
 * Shown on uncategorized transactions that have a `merchant` value.
 * Looks through the cached transaction list for the most recently used
 * category for the same merchant and surfaces it as a quick-apply chip.
 *
 * AC5 hard constraint: Never auto-applies — user must click Apply or Edit.
 */
import { useMemo } from 'react';
import { Check, Pencil, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAppSelector } from '@/features/hooks';
import type { Transaction } from '@/lib/types';

interface SuggestionChipProps {
  transaction: Transaction;
  onApply: (categoryId: string, categoryName: string) => void;
  onEdit: (suggestedCategoryId: string, suggestedCategoryName: string) => void;
}

/**
 * Derives the most recently used category for `merchant` from the cached
 * transactions list. Returns null if no prior history exists.
 */
function useMerchantSuggestion(merchant: string): { categoryId: string; categoryName: string } | null {
  const allTransactions = useAppSelector((s) => s.transactions.transactions);

  return useMemo(() => {
    if (!merchant) return null;

    const merchantLower = merchant.toLowerCase();
    // Find transactions with same merchant that already have a category, sorted newest first
    const matches = allTransactions
      .filter(
        (t) =>
          t.merchant?.toLowerCase() === merchantLower &&
          t.categoryId &&
          t.category // categoryId + display name both required
      )
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    if (matches.length === 0) return null;

    const best = matches[0];
    return {
      categoryId: best.categoryId!,
      categoryName: best.category!,
    };
  }, [allTransactions, merchant]);
}

export function SuggestionChip({ transaction, onApply, onEdit }: SuggestionChipProps) {
  const suggestion = useMerchantSuggestion(transaction.merchant ?? '');

  // Gracefully suppress if no merchant history
  if (!suggestion || !transaction.merchant) return null;

  return (
    <div
      className="flex items-center gap-1.5 flex-wrap"
      data-testid={`suggestion-chip-${transaction.id}`}
    >
      <Badge
        variant="secondary"
        className="flex items-center gap-1 text-xs font-normal text-muted-foreground bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 max-w-[180px]"
      >
        <Sparkles className="size-3 shrink-0 text-amber-500" />
        <span className="truncate">
          Suggested: <span className="font-medium text-foreground">{suggestion.categoryName}</span>
        </span>
      </Badge>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950/30"
        onClick={() => onApply(suggestion.categoryId, suggestion.categoryName)}
        data-testid={`suggestion-apply-${transaction.id}`}
      >
        <Check className="size-3 mr-1" />
        Apply
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-2 text-xs"
        onClick={() => onEdit(suggestion.categoryId, suggestion.categoryName)}
        data-testid={`suggestion-edit-${transaction.id}`}
      >
        <Pencil className="size-3 mr-1" />
        Edit
      </Button>
    </div>
  );
}
